import { inject, Injectable } from '@angular/core';
import { AnalyticsService } from '@app/core/analytics/analytics.service';
import { AnalyticsKeyEnum } from '@app/core/enums/analytics/analytics-key.enum';
import { ActionEnum } from '@app/core/enums/analytics/analytics-value.enum';
import { DocumentType } from '@app/core/enums/document/document-type.enum';
import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { AnalyticsEvent } from '@app/core/model/entities/analytics/analytics-event';
import { AssetGroup, AssetGroupInput } from '@app/core/model/entities/asset-group/asset-group';
import { Asset } from '@app/core/model/entities/asset/asset';
import { Document } from '@app/core/model/entities/document/document';
import {
  AddExistingAssetsModalComponent
} from '@app/features/main/views/asset-groups/asset-group-sheet/assets/modals/add-existing-assets-modal/add-existing-assets-modal.component';
import { AssetGroupsService } from '@app/features/main/views/asset-groups/asset-groups.service';
import {
  AssetGroupCreateModalComponent
} from '@app/features/main/views/asset-groups/modals/asset-create-modal/asset-group-create-modal.component';
import { AssetsService } from '@app/features/main/views/assets/assets.service';
import { DocumentModalService } from '@app/features/main/views/organization-documents/modals/document-modal.service';
import { SpinnerService } from '@app/shared/popup/spinner.service';
import { TranslateService } from '@ngx-translate/core';
import { PopupManager, PopupSize } from '@services/managers/popup.manager';
import { EMPTY, finalize, Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';

@Injectable()
export class AssetGroupModalService {

  private readonly popupManager = inject(PopupManager);
  private readonly assetGroupsService = inject(AssetGroupsService);
  private readonly assetsService = inject(AssetsService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly spinnerService = inject(SpinnerService);
  private readonly translate = inject(TranslateService);
  private readonly documentModalService = inject(DocumentModalService);

  /**
   * Open the dialog to create an Asset Group.
   * @return Created Asset Group.
   */
  public openCreateAssetGroupDialog(): Observable<AssetGroup> {
    const dialogRef = this.popupManager.showGenericPopup(AssetGroupCreateModalComponent, PopupSize.MEDIUM, {});
    return dialogRef.afterClosed()
      .pipe(
        switchMap(dialogResponse => {
          const analyticsEvent = new AnalyticsEvent(ActionEnum.CREATE, EntityTypeEnum.ASSET_GROUP);

          if (dialogResponse === 'yes') {
            analyticsEvent.addProperties({[AnalyticsKeyEnum.DIALOG_ACTION]: ActionEnum.SAVE});
            this.analyticsService.trackEvent(analyticsEvent);

            const generatedAssetGroup = dialogRef.componentInstance.getGeneratedObject();
            this.spinnerService.showSpinner();
            return this.assetGroupsService.createAssetGroup(generatedAssetGroup)
              .pipe(finalize(() => this.spinnerService.hideSpinner()));
          } else {
            analyticsEvent.addProperties({[AnalyticsKeyEnum.DIALOG_ACTION]: ActionEnum.CANCEL});
            this.analyticsService.trackEvent(analyticsEvent);
            return EMPTY;
          }
        })
      );
  }

  /**
   * Open the dialog to add existing Assets to an Asset Group.
   * @param assetGroup Asset Group the selected Assets will be added to.
   * @return The updated Assets.
   */
  public openAddExistingAssetsDialog(assetGroup: AssetGroup): Observable<Asset[]> {
    const dialogRef = this.popupManager.showGenericPopup(
      AddExistingAssetsModalComponent,
      PopupSize.MEDIUM,
      {assetGroup}
    );

    return dialogRef.afterClosed()
      .pipe(
        switchMap((dialogResponse: { response: string, selectedAssets?: Asset[] }) => {
          if (dialogResponse.response === 'yes') {
            if (dialogResponse.selectedAssets.length === 0) return EMPTY;
            dialogResponse.selectedAssets.map(asset => asset.id).forEach(assetId => {
                const analyticsEvent = new AnalyticsEvent(ActionEnum.UPDATE, EntityTypeEnum.ASSET);
                analyticsEvent.addProperties({
                  [AnalyticsKeyEnum.DIALOG_ACTION]: ActionEnum.UPDATE,
                  [AnalyticsKeyEnum.ENTITY_ID]: assetId
                });
                this.analyticsService.trackEvent(analyticsEvent);
              }
            );

            const assetsToUpdate = dialogResponse.selectedAssets.map(asset => ({
              asset,
              assetInput: {
                assetGroupIds: Array.from(new Set([...(asset.assetGroupIds ?? []), assetGroup.id]))
              }
            }));

            this.spinnerService.showSpinner();
            return this.assetsService.updateAssets(assetsToUpdate)
              .pipe(finalize(() => this.spinnerService.hideSpinner()));
          } else {
            const analyticsEvent = new AnalyticsEvent(ActionEnum.UPDATE, EntityTypeEnum.ASSET);
            analyticsEvent.addProperties({[AnalyticsKeyEnum.DIALOG_ACTION]: ActionEnum.CANCEL});
            this.analyticsService.trackEvent(analyticsEvent);
            return EMPTY;
          }
        })
      );
  }

  /**
   * Display a dialog for the User to upload files and create Documents related to an Asset Group entity.
   * @param assetGroupId ID of the Asset Group to link Documents to.
   * @return Created Documents.
   */
  public openUploadAssetGroupDocumentsDialog(assetGroupId: string): Observable<Document[]> {
    return this.documentModalService.openUploadEntityDocumentsDialog(
      assetGroupId,
      EntityTypeEnum.ASSET_GROUP,
      DocumentType.DOCUMENT
    );
  }

  /**
   * Display a dialog asking the User to confirm an Asset Group's Document's deletion.
   * @param document Document to delete.
   * @return True if the Document was successfully deleted, false if the operation was canceled.
   */
  public openDeleteAssetGroupDocumentDialog(document: Document): Observable<boolean> {
    return this.documentModalService.openDeleteEntityDocumentDialog(document);
  }

  /**
   * Display a dialog asking the User to confirm Asset Groups' deletion, then delete the Asset Groups if confirmed.
   * @param assetGroups Asset Groups to delete.
   * @return True if the Asset Groups were successfully deleted, false otherwise.
   * @throws Any encountered errors.
   */
  public openDeleteAssetGroupsDialog(assetGroups: AssetGroup[]): Observable<boolean> {
    const dialogRef = this.popupManager.showOkCancelPopup({
      dialogTitle: this.translate.instant(assetGroups.length === 1 ? 'TITLE.DELETE_ASSET_GROUP' : 'TITLE.DELETE_ASSET_GROUPS'),
      dialogMessage: this.translate.instant(assetGroups.length === 1 ? 'LABEL.DELETE_ASSET_GROUP' : 'LABEL.DELETE_ASSET_GROUPS'),
      type: 'warning',
      okText: this.translate.instant('LABEL.DELETE')
    });

    return dialogRef.afterClosed().pipe(
      switchMap(dialogResponse => {
        const event = new AnalyticsEvent(ActionEnum.DELETE, EntityTypeEnum.ASSET_GROUP);

        if (dialogResponse === 'yes') {
          event.addProperties({
            [AnalyticsKeyEnum.DIALOG_ACTION]: ActionEnum.DELETE,
            [AnalyticsKeyEnum.ENTITY_ID]: assetGroups.map(assetGroup => assetGroup.id).toString()
          });
          this.analyticsService.trackEvent(event);
          this.spinnerService.showSpinner();
          return this.assetGroupsService.deleteAssetGroups(assetGroups)
            .pipe(finalize(() => this.spinnerService.hideSpinner()));
        } else {
          event.addProperties({[AnalyticsKeyEnum.DIALOG_ACTION]: ActionEnum.CANCEL});
          this.analyticsService.trackEvent(event);
          return EMPTY;
        }
      })
    );
  }
}
